/*
 * useVisibleInterval — a polling hook that pauses when the browser tab
 * is hidden, reducing unnecessary API calls and saving bandwidth.
 *
 * Usage:
 *   useVisibleInterval(fetchData, 5000);
 *   // Calls fetchData() immediately, then every 5 s while tab is visible.
 *   // Pauses when tab is hidden, resumes immediately when visible again.
 *
 * ! file hooks/useVisibleInterval.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2025
 */

import { useEffect, useRef } from "react";

/**
 * @param {() => void|Promise<void>} callback  Function to call periodically
 * @param {number} intervalMs                  Milliseconds between calls
 * @param {boolean} [enabled=true]             Set false to disable entirely
 */
export default function useVisibleInterval(callback, intervalMs, enabled = true) {
  const savedCb = useRef(callback);

  // Keep latest callback ref without re-running effect
  useEffect(() => {
    savedCb.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled) return;

    let timer = null;
    let active = true;

    const tick = () => {
      if (active) savedCb.current();
    };

    const start = () => {
      tick(); // immediate call on resume
      clearInterval(timer);
      timer = setInterval(tick, intervalMs);
    };

    const stop = () => {
      clearInterval(timer);
      timer = null;
    };

    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    };

    // Start polling
    start();

    // Listen for visibility changes
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      active = false;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [intervalMs, enabled]);
}
